import { Clear } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@mui/material";
import {
  ChangeEvent,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
} from "react";
import enumLabel from "../../../utils/labels/enumLabel";
import { isArray, isNumber } from "lodash";
import { useTranslation } from "react-i18next";
import { testIds } from "./EnumSelect.testIds";

type EnumSelectProps<T extends string | number> = Omit<
  TextFieldProps,
  "onChange"
> & {
  enumObject: { [key: string]: T };
  onChange?: (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    value: T | null
  ) => void;
  optionLabel?: (t: T) => string;
  value?: T | null;
  multiple?: false;
  clearable?: boolean;
  withNoneOption?: boolean;
  noneOptionLabel?: string;
};

type EnumMultiSelectProps<T extends string | number> = Omit<
  TextFieldProps,
  "onChange"
> & {
  enumObject: { [key: string]: T };
  onChange?: (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    value: T[]
  ) => void;
  optionLabel?: (t: T) => string;
  value?: T[] | null;
  multiple: true;
  clearable?: boolean;
  withNoneOption?: boolean;
  noneOptionLabel?: string;
};

function EnumSelect<T extends string | number>({
  enumObject,
  optionLabel,
  onChange,
  multiple,
  clearable,
  withNoneOption = false,
  noneOptionLabel,
  ...props
}: EnumSelectProps<T> | EnumMultiSelectProps<T>) {
  const { t } = useTranslation("common");
  // Forward a data-testid onto the visible Select trigger (SelectDisplayProps)
  // instead of the wrapper, so tests can target it directly. Extracted so it
  // doesn't also land on the root and create a duplicate id.
  const { "data-testid": dataTestId, ...restProps } = props as typeof props & {
    "data-testid"?: string;
  };
  const inputDataTestId =
    restProps.inputProps?.["data-testid"] ??
    (dataTestId ? `${dataTestId}-input` : undefined);
  const hasValue = isArray(props.value)
    ? props.value && props.value.length
    : props.value;
  return (
    <TextField
      select
      {...restProps}
      value={
        hasValue
          ? props.value
          : multiple
          ? withNoneOption
            ? ["NONE"]
            : []
          : withNoneOption
          ? "NONE"
          : ""
      }
      onChange={(event) => {
        if (multiple) {
          const value = event.target.value as unknown as Array<T | "NONE">;
          onChange?.(
            event,
            value[value.length - 1] === "NONE"
              ? []
              : (value.filter((v) => v !== "NONE") as Array<T>)
          );
        } else {
          onChange?.(
            event,
            event.target.value === "NONE" ? null : (event.target.value as T)
          );
        }
      }}
      SelectProps={{
        multiple,
        ...(dataTestId
          ? {
              SelectDisplayProps: {
                "data-testid": dataTestId,
              } as HTMLAttributes<HTMLDivElement>,
            }
          : {}),
        IconComponent: !clearable || hasValue ? () => null : undefined,
        endAdornment:
          clearable && hasValue ? (
            <InputAdornment position="end">
              <IconButton data-testid={
                  dataTestId ? `${dataTestId}-clear` : testIds.enumSelectClear
                }
                onClick={(event) => {
                  if (multiple) {
                    onChange?.(event, []);
                  } else {
                    onChange?.(event, null);
                  }
                }}
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          ) : null,
      }}
      InputProps={{}}
      inputProps={{
        ...restProps.inputProps,
        ...(inputDataTestId ? { "data-testid": inputDataTestId } : {}),
      }}
    >
      {Object.values(enumObject)
        .filter((option) => !isNumber(option))
        .map((option) => (
          <MenuItem
            data-testid={
              dataTestId
                ? `${dataTestId}-option-${String(option).toLowerCase()}`
                : testIds.enumSelectOption
            }
            key={option}
            value={option}
          >
            {optionLabel ? optionLabel(option) : enumLabel(String(option))}
          </MenuItem>
        ))}
      {withNoneOption && (
        <MenuItem data-testid={
            dataTestId ? `${dataTestId}-option-none` : testIds.enumSelectNone
          } key={"NONE"} value={"NONE"}>
          {noneOptionLabel || t("none", "None")}
        </MenuItem>
      )}
    </TextField>
  );
}

export default EnumSelect;
